import React, { useEffect } from 'react';
import { Platform } from 'react-native';
import { PostHogProvider, usePostHog } from 'posthog-react-native';
import { analyticsService } from '../services/analytics';

const POSTHOG_API_KEY = process.env.EXPO_PUBLIC_POSTHOG_API_KEY || '';

// Type for the capture function that matches our analytics service interface
type CaptureFunction = {
  capture: (event: string, properties?: Record<string, string | number | boolean | null | undefined>) => void;
};

const AnalyticsInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const posthog = usePostHog();

  useEffect(() => {
    if (posthog) {
      // Cast to our interface - PostHog's capture accepts broader types than we use
      analyticsService.initialize(posthog as unknown as CaptureFunction);
    }
  }, [posthog]);

  return <>{children}</>;
};

export const PostHogInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  if (!POSTHOG_API_KEY) {
    if (__DEV__) {
      console.warn('[PostHog] No API key configured - analytics disabled');
    }
    return <>{children}</>;
  }

  return (
    <PostHogProvider
      apiKey={POSTHOG_API_KEY}
      options={{
        host: Platform.OS === 'web' ? '/api/ingest' : 'https://eu.i.posthog.com',
        persistence: 'memory',
        disabled: __DEV__,
        captureAppLifecycleEvents: false,
      }}
      autocapture={{
        // Screen tracking is handled manually in AppNavigator via analyticsService.trackScreenView()
        // Automatic capture causes errors because PostHogProvider is outside NavigationContainer
        captureScreens: false,
      }}
    >
      <AnalyticsInitializer>{children}</AnalyticsInitializer>
    </PostHogProvider>
  );
};
